import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retentionCodeApi } from "./axios";
import {
  ServiceResponseI,
  RetentionCodeI,
  RetentionCodeInputI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const handleErr = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error)?.message ?? "Error");
};

const getRetentionCodes = async (
  tipoImpuesto?: string,
): Promise<ServiceResponseI<RetentionCodeI[]>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await retentionCodeApi.get<
      ServiceResponseI<RetentionCodeI[]>
    >("", {
      params: tipoImpuesto ? { tipoImpuesto } : {},
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

const createRetentionCode = async (payload: RetentionCodeInputI) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await retentionCodeApi.post("", payload, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

const updateRetentionCode = async (
  id: string | number,
  payload: RetentionCodeInputI,
) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await retentionCodeApi.put(`/${id}`, payload, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

const deleteRetentionCode = async (id: string | number) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await retentionCodeApi.delete(`/${id}`, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useRetentionCodes = (tipoImpuesto?: string) => {
  const queryClient = useQueryClient();
  const key = ["retention-codes", { tipoImpuesto }];

  const retentionCodesQuery = useQuery(key, () =>
    getRetentionCodes(tipoImpuesto),
  );

  const invalidate = () => queryClient.invalidateQueries(["retention-codes"]);

  const createMutation = useMutation(
    (payload: RetentionCodeInputI) => createRetentionCode(payload),
    { onSuccess: invalidate },
  );

  const updateMutation = useMutation(
    ({ id, payload }: { id: string | number; payload: RetentionCodeInputI }) =>
      updateRetentionCode(id, payload),
    { onSuccess: invalidate },
  );

  const deleteMutation = useMutation(
    (id: string | number) => deleteRetentionCode(id),
    { onSuccess: invalidate },
  );

  return {
    retentionCodesQuery,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
